import { FCX } from "@datassential/platform-ui-lib";
import HomeHeader from "@pages/Home/components/HomeHeader/HomeHeader";
import SelectShow from "@pages/Home/components/HomeHeader/SelectShow";
import HomeSubHeader from "@pages/Home/components/HomeHeader/HomeSubHeader";
import { useMemo } from "react";
import SelectAmong from "@vms/SelectAmong";
import {
    HomePeopleRawNormDataItem,
    HomePeopleRawNormRequestPayload,
    useHomePeopleRawNormData
} from "@api/useHomePeopleRawNormData";
import { useFilteredKeywordsData } from "@api/useFilteredKeywordsData";
import { useDisplayMetricContext, useHomeContext, useHomeSearchSettingsContext } from "@pages/Home/HomeContext";
import { Sort } from "@models";
import TableSortingContext, { TableSortingContextType } from "@components/Table/TableSorting";
import { CopyTableOptions, useCopyTableDataWithFilters } from "@vms/CopyTable/useCopyTableDataWithFilters";
import {
    PeopleViewRawNormTableConfigProps,
    usePeopleViewRawNormTableConfig
} from "@pages/Home/components/PeopleView/PeopleViewRawNormTableConfig";
import { TableColumnsContext } from "@components/Table/TableColumns";
import WindowScrollerTable from "@components/Table/WindowScrollerTable";
import AsideButtons, { CopyTableButton } from "@components/AsideButtons";
import { PeopleViewFilterSortContext, usePeopleViewContext } from "@pages/Home/components/PeopleView/PeopleViewContext";
import { ALL_RESPONDENT_TYPES } from "@models/RespondentType";
import SelectDisplayMetric from "@vms/SelectDisplayMetric";
import { HomeShow } from "@core/constants";
import { useMetric } from "@core/CommonDataContext";
import { createFilterSortWithContext } from "@hooks/useFilterSortWithContext";
import { Metric } from "@models/Metric";

const useFilterSortWithContext = createFilterSortWithContext<HomePeopleRawNormDataItem>(PeopleViewFilterSortContext);

const PeopleViewRawNorm: FCX = () => {
    const { resetSearch } = useHomeSearchSettingsContext();
    const { show, setShow } = usePeopleViewContext();
    const { displayMetricId, setDisplayMetricId } = useDisplayMetricContext();
    const { search } = useHomeContext();


    const payload = useMemo(
        (): HomePeopleRawNormRequestPayload => ({
            respondentTypes: ALL_RESPONDENT_TYPES as HomePeopleRawNormRequestPayload["respondentTypes"],
            metric: displayMetricId as HomePeopleRawNormRequestPayload["metric"],
        }),
        [displayMetricId]
    );

    const { data: rawData, isLoading } = useHomePeopleRawNormData(payload);

    const { getFinalData, dataForSorting } = useFilteredKeywordsData(rawData, search);

    const isBaseSizeView = useMemo(
        () => displayMetricId === null,
        [displayMetricId]
    );

    const mappedDataForSorting = useMemo(
        () => dataForSorting.map(i => {
            const columns: { [key: string]: number | null } = {};

            i.columns.forEach((i) => {
                if (!isBaseSizeView && show === HomeShow.Norm) {
                    columns[`column${i.id}`] = i.norm ?? null;
                } else {
                    columns[`column${i.id}`] = i.sortValue;
                }
            });

            return {
                ...i,
                ...columns,
            };
        }),
        [dataForSorting, isBaseSizeView, show]
    );

    const {
        data: sortedData,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useFilterSortWithContext(
        mappedDataForSorting,
    );

    const data = useMemo(
        () => getFinalData(sortedData),
        [getFinalData, sortedData]
    );

    const tableConfigProps = useMemo(
        (): PeopleViewRawNormTableConfigProps => ({
            data,
        }),
        [data],
    );

    const tableConfig = usePeopleViewRawNormTableConfig(tableConfigProps);

    const tableProviderValue = useMemo(
        () => ({
            data: tableConfig,
        }),
        [tableConfig],
    );

    const tableSortingProviderValue = useMemo(
        (): TableSortingContextType => ({
            value: {
                id: sortingKey,
                direction: sortingDirection,
            },
            options: [
                { id: 'name', name: 'name', defaultDirection: Sort.Asc, },
                { id: 'macStage', name: 'macStage', },
                ...ALL_RESPONDENT_TYPES.map(id => ({
                    id: `column${id}`,
                    name: `column${id}`,
                })),
            ],
            setValue: ({ id, direction }) => {
                setSortingKey(id as keyof HomePeopleRawNormDataItem);
                setSortingDirection(direction);
            },
        }),
        [sortingKey, sortingDirection, setSortingKey, setSortingDirection]
    );

    const displayMetric = useMetric(displayMetricId);

    const copyTableOptions = useMemo(
        (): CopyTableOptions => ({
            hideAmong: true,
            additionalRows: [[`Metric: ${displayMetric ? displayMetric?.name : 'Base'}`]]
        }),
        [displayMetric]
    );

    const copyTableContent = useCopyTableDataWithFilters(tableConfig, data.length, copyTableOptions);

    const disableShow = useMemo(() => displayMetricId === Metric.PolarityScore, [displayMetricId]);

    return (
        <>
            <HomeHeader resultCount={dataForSorting.length} isLoading={isLoading}>
                <SelectShow show={show} setShow={setShow} disabled={disableShow}/>
            </HomeHeader>
            <HomeSubHeader>
                <SelectDisplayMetric
                    value={displayMetricId}
                    setValue={setDisplayMetricId}
                    onChange={resetSearch}
                />
                <SelectAmong
                    label="FOR:"
                    defaultThumbnailText="Please select"
                    showTotalOption
                    useOptionsLimit
                    noBrands
                    allowMultiBrands
                    onApply={resetSearch}
                />
            </HomeSubHeader>
            <TableSortingContext.Provider value={tableSortingProviderValue}>
                <TableColumnsContext.Provider value={tableProviderValue}>
                    <WindowScrollerTable
                        rowsCount={isLoading ? 0 : data.length}
                        headHeight={48}
                        rowHeight={42}
                        isLoading={isLoading}
                        showFallback
                    >
                        <AsideButtons>
                            <CopyTableButton data={copyTableContent}/>
                        </AsideButtons>
                    </WindowScrollerTable>
                </TableColumnsContext.Provider>
            </TableSortingContext.Provider>
        </>
    );
};

export default PeopleViewRawNorm;